import React from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { Button, Form, FormGroup } from 'reactstrap';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import intl from 'react-intl-universal';

import Icon from 'components/Icon';
import Radio from 'components/Radio';
import Page from 'components/Page';
import PageHeader from 'components/PageHeader';
import PageFooter from 'components/PageFooter';
import FormBlock from 'modules/forms/components/FormBlock';
import { EAppointmentFormStatus, IForm } from 'modules/forms/interfaces/form.interfaces';
import { postAppointmentForm, setFormData } from 'modules/forms/actions/form.actions';
import { infectedInsectBitesRoutes } from 'modules/forms/constants/routes.constants';
import { useTypedSelector } from 'hooks/useTypedSelector';
import ImmunosuppressedTooltip from 'modules/forms/components/ImmunosuppressedTooltip';
import { getFormResultHTML } from 'modules/forms/helpers/formResult.helper';
import FormResult from './FormResult';

const GeneralQualifier = (): JSX.Element => {
  const history = useHistory();
  const dispatch = useDispatch();
  const appointment = useTypedSelector(state => state.form.appointment);
  const stateForm = useTypedSelector(state => state.form.form);
  const postLoading = useTypedSelector(state => state.form.loading.postForm);
  const methods = useForm<IForm>({
    defaultValues: {
      passesGeneralQualification: stateForm.passesGeneralQualification,
    },
    mode: 'onSubmit',
  });
  const { control, watch, handleSubmit } = methods;
  const passesGeneralQualification = watch('passesGeneralQualification');

  const onBack = () => {
    history.goBack();
  };

  const onSubmit = (values: IForm) => {
    dispatch(setFormData(values));
    if (!values.passesGeneralQualification) {
      dispatch(
        postAppointmentForm({
          form: {
            appointmentUuid: appointment?.uuid,
            status: EAppointmentFormStatus.TO_GP,
            response: getFormResultHTML({ ...stateForm, ...values }, FormResult),
          },
          route: infectedInsectBitesRoutes.disqualification,
        }),
      );
    } else {
      history.push({
        pathname: infectedInsectBitesRoutes.secondaryScreening,
        search: history.location.search,
      });
    }
  };

  return (
    <Page>
      <PageHeader step={1} maxSteps={5} />
      <FormProvider {...methods}>
        <Form className="page" onSubmit={handleSubmit(onSubmit)}>
          <div className="page-content">
            <main id="page-main-scrollable" className="page-main">
              <FormBlock
                title="General"
                description={
                  <>
                    Is the patient for which you have booked this Infected Insect Bite(s) consult:
                    <ul>
                      <li>Under the age of 1 year</li>
                      <li>Pregnant and under the age of 16 years</li>
                      <li>
                        Immunosuppressed <ImmunosuppressedTooltip />
                      </li>
                    </ul>
                  </>
                }
              >
                <Controller
                  name="passesGeneralQualification"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormGroup>
                      <Radio
                        checked={value === false}
                        onChange={() => onChange(false)}
                        label="Yes, the patient meets one or more of the above criteria"
                      />
                      <Radio
                        checked={value === true}
                        onChange={() => onChange(true)}
                        label="No, the patient does not meet one or more of the above criteria"
                      />
                    </FormGroup>
                  )}
                />
              </FormBlock>
            </main>

            <PageFooter>
              <Button
                color="primary"
                outline
                type="button"
                block
                disabled={postLoading}
                onClick={onBack}
              >
                <Icon iconName="arrow-left" />
                {intl.get('button.back')}
              </Button>
              <Button
                color="primary"
                type="button"
                block
                disabled={postLoading || passesGeneralQualification === undefined}
                onClick={handleSubmit(onSubmit)}
              >
                {intl.get('button.next')} <Icon iconName="arrow-right" />
              </Button>
            </PageFooter>
          </div>
        </Form>
      </FormProvider>
    </Page>
  );
};

export default GeneralQualifier;
